import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Accordion, Button, Col, Container, Dropdown, Form, Modal, Offcanvas, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Navbar from '../../Navbar';
import PageTitle from '../../components/PageTitle';
import ShareGuideModal from './ShareGuideModal';
import PublishGuideModal from './PublishGuideModal';
import { authHeaders, formatGuideDate, guidesFetch } from './guidesUtils';
import GuideItemIcon from './GuideItemIcon';
import './guides.css';

function clearTopicDragUi() {
  document
    .querySelectorAll('.guides-topic-row-item.is-dragging, .guides-topic-row-item.drop-before, .guides-topic-row-item.drop-after, .guides-topic-drop-empty.is-drop-target')
    .forEach((el) => el.classList.remove('is-dragging', 'drop-before', 'drop-after', 'is-drop-target'));
}

const OPEN_MODE_KEY = 'guides-notebook-open-mode';

function readStoredOpenMode() {
  try {
    return localStorage.getItem(OPEN_MODE_KEY) === 'edit' ? 'edit' : 'read';
  } catch {
    return 'read';
  }
}

function NotebookSwitcher({ notebooks, activeId, onSelect }) {
  return (
    <nav className="guides-notebook-switcher">
      {notebooks.length === 0 && (
        <div className="text-muted small px-1">No notebooks yet</div>
      )}
      {notebooks.map((nb) => (
        <button
          key={nb.id}
          type="button"
          className={`guides-notebook-switcher-item${Number(nb.id) === Number(activeId) ? ' active' : ''}`}
          onClick={() => onSelect(nb)}
        >
          <div className="guides-notebook-switcher-thumb">
            <GuideItemIcon item={nb} kind="notebook" />
          </div>
          <div className="guides-notebook-switcher-meta min-w-0">
            <span className="guides-notebook-switcher-title">{nb.title}</span>
            <span className="guides-notebook-switcher-date">
              {formatGuideDate(nb.createdAt)}
            </span>
          </div>
        </button>
      ))}
    </nav>
  );
}

function markTopicDrop(rowEl, after) {
  document
    .querySelectorAll('.guides-topic-row-item.drop-before, .guides-topic-row-item.drop-after, .guides-topic-drop-empty.is-drop-target')
    .forEach((el) => el.classList.remove('drop-before', 'drop-after', 'is-drop-target'));
  if (rowEl) rowEl.classList.add(after ? 'drop-after' : 'drop-before');
}

export default function NotebookManagePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [notebook, setNotebook] = useState(null);
  const [myNotebooks, setMyNotebooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSections, setActiveSections] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [sectionModal, setSectionModal] = useState(false);
  const [sectionTitle, setSectionTitle] = useState('');
  const [topicModal, setTopicModal] = useState(null);
  const [topicTitle, setTopicTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [openMode, setOpenMode] = useState(readStoredOpenMode);
  const [notebooksOpen, setNotebooksOpen] = useState(false);
  const topicDragRef = useRef(null);

  const loadMine = useCallback(async () => {
    try {
      const data = await guidesFetch('/mine', { headers: authHeaders() });
      setMyNotebooks((Array.isArray(data) ? data : []).filter((item) => item.type === 'notebook'));
    } catch {
      setMyNotebooks([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await guidesFetch(`/notebooks/${id}`, { headers: authHeaders() });
      setNotebook(data);
      const keys = (data.sections || []).map((s) => String(s.id));
      setActiveSections(keys);
    } catch (err) {
      toast.error(err.message || 'Failed to load notebook');
      navigate('/guides/mine');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
    loadMine();
  }, [load, loadMine]);

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_MODE_KEY, openMode);
    } catch {
      // Private browsing can block storage; the toggle still works for this visit.
    }
  }, [openMode]);

  const togglePublish = async () => {
    if (!notebook.published) {
      setPublishOpen(true);
      return;
    }
    if (!window.confirm('Unpublish this notebook?')) return;
    try {
      const res = await guidesFetch(`/notebooks/${id}/publish`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ published: false })
      });
      setNotebook((n) => ({
        ...n,
        published: res.published,
        publishedAt: res.publishedAt,
        allowEdits: res.allowEdits,
        categoryId: res.categoryId,
        topicId: res.topicId,
        description: res.description ?? n.description
      }));
      toast.success('Unpublished');
      loadMine();
    } catch (err) {
      toast.error(err.message || 'Failed to update publish status');
    }
  };

  const addSection = async (e) => {
    e.preventDefault();
    if (!sectionTitle.trim()) return;
    setBusy(true);
    try {
      await guidesFetch(`/notebooks/${id}/sections`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ title: sectionTitle.trim() })
      });
      setSectionModal(false);
      setSectionTitle('');
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to add section');
    } finally {
      setBusy(false);
    }
  };

  const deleteSection = async (sectionId) => {
    if (!window.confirm('Delete this section and all its topics?')) return;
    try {
      await guidesFetch(`/sections/${sectionId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to delete section');
    }
  };

  const addTopic = async (e) => {
    e.preventDefault();
    if (!topicTitle.trim() || !topicModal) return;
    setBusy(true);
    try {
      const topic = await guidesFetch(`/sections/${topicModal}/topics`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ title: topicTitle.trim() })
      });
      setTopicModal(null);
      setTopicTitle('');
      navigate(`/guides/notebooks/${id}/topics/${topic.id}/edit`);
    } catch (err) {
      toast.error(err.message || 'Failed to add topic');
      setBusy(false);
    }
  };

  const deleteTopic = async (topicId) => {
    if (!window.confirm('Delete this topic?')) return;
    try {
      await guidesFetch(`/topics/${topicId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to delete topic');
    }
  };

  const commitTopicMove = async (toSectionId, beforeTopicId, after) => {
    const drag = topicDragRef.current;
    topicDragRef.current = null;
    clearTopicDragUi();
    if (!drag || !notebook) return;

    const same = Number(drag.sectionId) === Number(toSectionId);
    const fromSection = (notebook.sections || []).find((s) => Number(s.id) === Number(drag.sectionId));
    const toSection = (notebook.sections || []).find((s) => Number(s.id) === Number(toSectionId));
    if (!fromSection || !toSection) return;

    const sortTopics = (list) => [...(list || [])].sort((a, b) => a.sortOrder - b.sortOrder);
    const fromTopics = sortTopics(fromSection.topics);
    const toTopics = same ? fromTopics : sortTopics(toSection.topics);
    if (!fromTopics.some((t) => t.id === drag.topicId)) return;

    const destIds = toTopics.map((t) => t.id);
    let insertAt = destIds.length;
    if (beforeTopicId != null) {
      const idx = destIds.indexOf(beforeTopicId);
      if (idx >= 0) insertAt = after ? idx + 1 : idx;
    }

    let nextToIds;
    if (same) {
      const fromIdx = destIds.indexOf(drag.topicId);
      if (fromIdx < 0) return;
      if (insertAt === fromIdx || insertAt === fromIdx + 1) return;
      nextToIds = [...destIds];
      nextToIds.splice(fromIdx, 1);
      if (insertAt > fromIdx) insertAt -= 1;
      nextToIds.splice(insertAt, 0, drag.topicId);
    } else {
      nextToIds = [...destIds];
      nextToIds.splice(insertAt, 0, drag.topicId);
    }

    const nextFromIds = same
      ? nextToIds
      : fromTopics.map((t) => t.id).filter((tid) => tid !== drag.topicId);

    setNotebook((n) => {
      if (!n) return n;
      const byTopic = {};
      (n.sections || []).forEach((s) => {
        (s.topics || []).forEach((t) => { byTopic[t.id] = t; });
      });
      return {
        ...n,
        sections: (n.sections || []).map((s) => {
          let ids = null;
          if (Number(s.id) === Number(toSectionId)) ids = nextToIds;
          else if (!same && Number(s.id) === Number(drag.sectionId)) ids = nextFromIds;
          if (!ids) return s;
          return {
            ...s,
            topics: ids
              .map((tid, i) => (byTopic[tid] ? { ...byTopic[tid], sectionId: s.id, sortOrder: i } : null))
              .filter(Boolean)
          };
        })
      };
    });

    try {
      await guidesFetch(`/sections/${toSectionId}/topics/reorder`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify({ order: nextToIds })
      });
      if (!same) {
        await guidesFetch(`/sections/${drag.sectionId}/topics/reorder`, {
          method: 'PUT',
          headers: authHeaders(true),
          body: JSON.stringify({ order: nextFromIds })
        });
      }
    } catch (err) {
      toast.error(err.message || 'Failed to move topic');
      await load();
    }
  };

  if (loading || !notebook) {
    return (
      <div className="guides-app guides-loading-screen">
        <Navbar pageTitle="Notebook" />
        <div className="guides-loading"><Spinner /></div>
      </div>
    );
  }

  const sections = [...(notebook.sections || [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const topicCount = sections.reduce((s, sec) => s + ((sec.topics || []).length), 0);
  const canEdit = !!notebook.canEdit;
  const selectNotebook = (nb) => {
    setNotebooksOpen(false);
    if (Number(nb.id) !== Number(id)) navigate(`/guides/notebooks/${nb.id}`);
  };
  const openTopic = (topicId) => {
    if (canEdit && openMode === 'edit') navigate(`/guides/notebooks/${id}/topics/${topicId}/edit`);
    else navigate(`/guides/notebooks/${id}/read/${topicId}`);
  };
  return (
    <div className="guides-app guides-topic-editor-page bg-inverse">
      <Navbar pageTitle={notebook.title} />
      <PageTitle title={notebook.title} />

      <Container fluid="lg" className="guides-topic-editor-workspace">
        <Row className="g-0 h-100">
          {canEdit && (
          <Col lg={3} md={3} className="border-end guides-topic-editor-rail d-none d-md-block">
            <div className="p-3 ps-1 pe-2">
              <div className="smaller text-uppercase text-muted fw-semibold  py-2">My Notebooks</div>
              <NotebookSwitcher notebooks={myNotebooks} activeId={id} onSelect={selectNotebook} />
            </div>
          </Col>
          )}

          <Col lg={canEdit ? 9 : 12} md={canEdit ? 9 : 12} className="guides-topic-editor-col">
            <div className="guides-read-scroll guides-manage-scroll pt-3 pe-0">
              <div className="guides-list-header">
                <div className="guides-manage-title min-w-0">
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="light-solid"
                      className="guides-icon-btn guides-notebooks-toggle d-md-none"
                      onClick={() => setNotebooksOpen(true)}
                      aria-label="My notebooks"
                      title="My notebooks"
                    >
                      <i className="bi bi-list" aria-hidden />
                    </Button>
                  )}
                  <div className="min-w-0">
                    <h6 className="mb-0 fw-semibold text-truncate">{notebook.title}</h6>
                    <p className="guides-page-meta">
                      {topicCount} topics · Updated {formatGuideDate(notebook.updatedAt)}
                    </p>
                  </div>
                </div>
                {canEdit && (
                  <div className="guides-list-actions">
                    <div className="radio-inputs" style={{ width: 'fit-content' }}>
                      <label className="radio">
                        <input
                          type="checkbox"
                          checked={!!notebook.published}
                          onChange={togglePublish}
                          aria-label={notebook.published ? 'Published' : 'Publish'}
                        />
                        <span className="name px-md-3 p-2">
                          <i className="bi bi-globe2" aria-hidden />
                          <span className="d-none d-md-inline ms-1">{notebook.published ? 'Published' : 'Publish'}</span>
                        </span>
                      </label>
                      <label className="radio">
                        <input
                          type="checkbox"
                          checked={(notebook.sharedUserIds || []).length > 0}
                          onChange={() => setShareOpen(true)}
                          aria-label="Shared"
                        />
                        <span className="name px-md-3 p-2">
                          <i className="bi bi-share" aria-hidden />
                          <span className="d-none d-md-inline ms-1">Shared</span>
                        </span>
                      </label>
                    </div>
                    <div className="radio-inputs" style={{ width: 'fit-content' }} role="radiogroup" aria-label="How topics open">
                      <label className="radio">
                        <input
                          type="radio"
                          name="notebook-open-mode"
                          checked={openMode === 'read'}
                          onChange={() => setOpenMode('read')}
                        />
                        <span className="name px-md-3 p-2">
                          <i className="bi bi-book" aria-hidden />
                          <span className="d-none d-md-inline ms-1">Read</span>
                        </span>
                      </label>
                      <label className="radio">
                        <input
                          type="radio"
                          name="notebook-open-mode"
                          checked={openMode === 'edit'}
                          onChange={() => setOpenMode('edit')}
                        />
                        <span className="name px-md-3 p-2">
                          <i className="bi bi-pencil" aria-hidden />
                          <span className="d-none d-md-inline ms-1">Edit</span>
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
              <div className="guides-sections-head">
                <span className="guides-section-label mb-0">Sections & Topics</span>
                {canEdit && (
                  <Button size="sm" variant="light-lite primary-subtle " onClick={() => setSectionModal(true)}>
                    <i className="bi bi-plus-lg me-1" /> Add Section
                  </Button>
                )}
              </div>

              {sections.length === 0 ? (
                <div className="guides-empty card border shadow-none p-4">Add a section to organize topics.</div>
              ) : (
                <Accordion
                  alwaysOpen
                  activeKey={activeSections}
                  onSelect={(keys) => setActiveSections(Array.isArray(keys) ? keys : keys ? [keys] : [])}
                  className="guides-accordion"
                >
                  {sections.map((section) => {
                    const topics = [...(section.topics || [])].sort((a, b) => a.sortOrder - b.sortOrder);
                    return (
                      <Accordion.Item
                        eventKey={String(section.id)}
                        key={section.id}
                        className="guides-section-card"
                        onDragOver={(e) => {
                          if (!canEdit || !topicDragRef.current) return;
                          e.preventDefault();
                          if (!activeSections.includes(String(section.id))) {
                            setActiveSections((keys) => (
                              keys.includes(String(section.id)) ? keys : [...keys, String(section.id)]
                            ));
                          }
                        }}
                        onDrop={(e) => {
                          if (!canEdit || !topicDragRef.current) return;
                          if (e.target.closest('.guides-topic-row-item, .guides-topic-drop-empty')) return;
                          e.preventDefault();
                          commitTopicMove(section.id, null, false);
                        }}
                      >
                        <div className="guides-section-card-header accordion-header">
                          <Accordion.Button className="guides-section-card-toggle">
                            <span className="fw-semibold">{section.title}</span>
                          </Accordion.Button>
                          <div className="guides-section-card-actions">
                            <span className="text-muted small text-nowrap me-2">
                              {topics.length} {topics.length === 1 ? 'topic' : 'topics'}
                            </span>
                            {canEdit && (
                              <>
                                <Button
                                  variant="light-lite primary-subtle"
                                  className="px-2 py-1"
                                  title="Add topic"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setTopicModal(section.id);
                                    setTopicTitle('');
                                  }}
                                >
                                  <i className="bi bi-plus-lg" />
                                </Button>
                                <Button
                                  variant="light-lite danger-lite"
                                  title="Delete section"
                                  className="px-2 py-1"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    deleteSection(section.id);
                                  }}
                                >
                                  <i className="bi bi-trash" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        <Accordion.Body className="guides-section-card-body p-0">
                          {topics.length === 0 ? (
                            <div
                              className="text-muted small px-3 py-3 guides-topic-drop-empty"
                              onDragOver={(e) => {
                                if (!canEdit || !topicDragRef.current) return;
                                e.preventDefault();
                                e.stopPropagation();
                                e.dataTransfer.dropEffect = 'move';
                                e.currentTarget.classList.add('is-drop-target');
                              }}
                              onDragLeave={(e) => {
                                e.currentTarget.classList.remove('is-drop-target');
                              }}
                              onDrop={(e) => {
                                if (!canEdit) return;
                                e.preventDefault();
                                e.stopPropagation();
                                commitTopicMove(section.id, null, false);
                              }}
                            >
                              {canEdit ? 'No topics yet. Drag a topic here, or add one to get started.' : 'No topics yet.'}
                            </div>
                          ) : (
                            <div className="guides-topic-rows">
                              {topics.map((topic) => (
                                <div
                                  key={topic.id}
                                  className="guides-topic-row-item custom-hover-bg-alt"
                                  onDragOver={(e) => {
                                    if (!canEdit || !topicDragRef.current) return;
                                    e.preventDefault();
                                    e.stopPropagation();
                                    e.dataTransfer.dropEffect = 'move';
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const after = e.clientY > rect.top + rect.height / 2;
                                    markTopicDrop(e.currentTarget, after);
                                  }}
                                  onDrop={(e) => {
                                    if (!canEdit || !topicDragRef.current) return;
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const after = e.clientY > rect.top + rect.height / 2;
                                    commitTopicMove(section.id, topic.id, after);
                                  }}
                                >
                                  {canEdit && (
                                  <span
                                    className="guides-topic-drag-handle"
                                    draggable
                                    title="Drag to another section or to reorder"
                                    aria-label="Drag to move topic"
                                    onDragStart={(e) => {
                                      topicDragRef.current = { sectionId: section.id, topicId: topic.id };
                                      e.dataTransfer.effectAllowed = 'move';
                                      e.dataTransfer.setData('text/plain', String(topic.id));
                                      e.currentTarget.closest('.guides-topic-row-item')?.classList.add('is-dragging');
                                    }}
                                    onDragEnd={() => {
                                      const drag = topicDragRef.current;
                                      setTimeout(() => {
                                        if (topicDragRef.current !== drag) return;
                                        topicDragRef.current = null;
                                        clearTopicDragUi();
                                      }, 0);
                                    }}
                                  >
                                    <i className="bi bi-grip-vertical" aria-hidden />
                                  </span>
                                  )}
                                  <button
                                    type="button"
                                    className="guides-topic-row-main"
                                    onClick={() => openTopic(topic.id)}
                                  >
                                    <span className="fw-semibold d-block">{topic.title}</span>
                                    <span className="text-muted small">{formatGuideDate(topic.updatedAt)}</span>
                                  </button>
                                  {canEdit ? (
                                  <Dropdown align="end" className="guides-topic-row-menu">
                                    <Dropdown.Toggle
                                      variant="light-lite"
                                      size="sm"
                                      className="guides-topic-menu-toggle px-2 py-1 no-arrow"
                                      id={`topic-menu-${topic.id}`}
                                    >
                                      <i className="bi bi-three-dots-vertical" aria-hidden />
                                      <span className="visually-hidden">Topic options</span>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                      <Dropdown.Item
                                        onClick={() => navigate(`/guides/notebooks/${id}/topics/${topic.id}/edit`)}
                                      >
                                        <i className="bi bi-pencil me-2" aria-hidden />
                                        Edit
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        className="text-danger"
                                        onClick={() => deleteTopic(topic.id)}
                                      >
                                        <i className="bi bi-trash me-2" aria-hidden />
                                        Remove
                                      </Dropdown.Item>
                                    </Dropdown.Menu>
                                  </Dropdown>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              )}
            </div>
          </Col>
        </Row>
      </Container>

      {canEdit && (
        <Offcanvas
          show={notebooksOpen}
          onHide={() => setNotebooksOpen(false)}
          placement="start"
          className="guides-notebooks-offcanvas"
          aria-labelledby="guides-notebooks-offcanvas-title"
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title id="guides-notebooks-offcanvas-title" className="smaller text-uppercase text-muted fw-semibold">
              My Notebooks
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <NotebookSwitcher notebooks={myNotebooks} activeId={id} onSelect={selectNotebook} />
          </Offcanvas.Body>
        </Offcanvas>
      )}

      <Modal show={sectionModal} onHide={() => setSectionModal(false)} centered>
        <Form onSubmit={addSection}>
          <Modal.Header closeButton><Modal.Title>Add Section</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Control
              value={sectionTitle}
              onChange={(e) => setSectionTitle(e.target.value)}
              placeholder="Section title"
              autoFocus
              required
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setSectionModal(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={busy}>Add</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={!!topicModal} onHide={() => setTopicModal(null)} centered>
        <Form onSubmit={addTopic}>
          <Modal.Header closeButton><Modal.Title>Add Topic</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Control
              value={topicTitle}
              onChange={(e) => setTopicTitle(e.target.value)}
              placeholder="Topic title"
              autoFocus
              required
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setTopicModal(null)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={busy}>Create & edit</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ShareGuideModal
        show={shareOpen}
        onHide={() => setShareOpen(false)}
        targetType="notebook"
        targetId={Number(id)}
        initialUserIds={notebook.sharedUserIds || []}
        onSaved={(shares) => setNotebook((n) => ({
          ...n,
          sharedUserIds: shares.map((s) => s.userId)
        }))}
      />
      <PublishGuideModal
        show={publishOpen}
        onHide={() => setPublishOpen(false)}
        targetType="notebook"
        targetId={Number(id)}
        initialDescription={notebook.description || ''}
        initialCategoryId={notebook.categoryId}
        initialTopicId={notebook.topicId}
        initialAllowEdits={!!notebook.allowEdits}
        onPublished={(res) => {
          setNotebook((n) => ({
            ...n,
            published: true,
            publishedAt: res.publishedAt,
            allowEdits: res.allowEdits,
            categoryId: res.categoryId,
            topicId: res.topicId,
            description: res.description ?? n.description,
            category: res.categoryName || n.category
          }));
          loadMine();
        }}
      />
    </div>
  );
}
